/**
 * Keeps the running page on the deployed build.
 *
 * - PWA / browser: the service worker serves the cached shell first, then autoUpdate swaps in the new
 *   one and reloads, but only once it notices. A resumed PWA, or a tab left open, never checks.
 * - iOS app (WKWebView): no service worker at all; the page stays on whatever JS it loaded until reloaded.
 *
 * So: compare our build with the server's (/healthz) on start, on every return to the foreground and
 * hourly. When it differs, update the service worker and reload. Coming back to the app reloads straight
 * away (nothing is mid-gesture); while in use, a toast offers the reload instead.
 */
import { onNativeEvent, isNative } from './native.js';
import { toast } from './toast.js';

const BUILD = __BUILD_ID__;
let offered = false;
let reloading = false;
let quietRetry: number | null = null;

/** Sign-in, redeem, forced password change (all .auth frames) and tablet pairing: no update toast there. */
const quietScreen = (): boolean => /^\/(login|redeem|pair)(\/|$)/.test(location.pathname) || document.querySelector('.auth') !== null;

async function serverBuild(): Promise<string | null> {
  try {
    const r = await fetch('/healthz', { cache: 'no-store', credentials: 'omit' });
    if (!r.ok) return null;
    return ((await r.json()) as { build?: string }).build ?? null;
  } catch {
    return null;
  }
}

async function reload(reg: ServiceWorkerRegistration | undefined): Promise<void> {
  if (reloading) return;
  reloading = true;
  // Let the new worker install and take over first, so the reload isn't answered from the old cache.
  if (reg) {
    try {
      await reg.update();
      const w = reg.installing ?? reg.waiting;
      if (w) {
        await new Promise<void>((done) => {
          const t = window.setTimeout(done, 8000);
          w.addEventListener('statechange', () => {
            if (w.state === 'activated') {
              window.clearTimeout(t);
              done();
            }
          });
        });
      }
    } catch {
      // Fall through to a plain reload.
    }
  }
  location.reload();
}

async function check(reg: ServiceWorkerRegistration | undefined, foreground: boolean): Promise<void> {
  if (reloading || BUILD === 'unknown' || document.visibilityState !== 'visible') return;
  const live = await serverBuild();
  if (!live || live === BUILD) return;
  // Auto-reload once per new build: if that didn't take (e.g. the worker failed to install), offer it instead.
  let tried: string | null = null;
  try {
    tried = sessionStorage.getItem('build-reload');
  } catch {
    // Storage blocked: treat as not tried.
  }
  if (foreground && tried !== live) {
    try {
      sessionStorage.setItem('build-reload', live);
    } catch {
      // Ignore.
    }
    await reload(reg);
    return;
  }
  if (offered) return;
  // Not over sign-in, password or pairing forms: ask again once the person is past them.
  if (quietScreen()) {
    if (quietRetry === null) quietRetry = window.setTimeout(() => ((quietRetry = null), void check(reg, false)), 30_000);
    return;
  }
  offered = true;
  toast('A new version is ready', { ttl: 60_000, action: { label: 'Reload', run: () => void reload(reg) } });
}

/** Reload onto the deployed build (letting the service worker, if any, install it first). Wall tablets (screens/tablet). */
export async function reloadToLatest(): Promise<void> {
  let reg: ServiceWorkerRegistration | undefined;
  try {
    reg = await navigator.serviceWorker?.getRegistration();
  } catch {
    // No service worker: a plain reload.
  }
  await reload(reg);
}

export function watchBuild(reg?: ServiceWorkerRegistration): void {
  void check(reg, true);
  document.addEventListener('visibilitychange', () => void check(reg, true));
  if (isNative()) onNativeEvent((e) => e.type === 'app.foreground' && void check(reg, true));
  window.setInterval(() => void check(reg, false), 60 * 60 * 1000);
}
